import { useState } from 'react';
import type { JSX } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import type { BrandItem } from '../lib/brands.js';
import { Panel } from '../components/ui.js';

/**
 * Odesílací identita podle skupiny zpráv (D-309). Adresy musí být ověřené ve SmartEmailingu
 * (Odesílací adresy); hub to nemůže ověřit, kontroluje jen doménu značky.
 */
const GROUPS: Array<[string, string, string]> = [
  ['orders', 'Objednávky, potvrzení a platby', 'S01, S09, S14, S18, T01 až T04, T10'],
  ['service', 'Servis, změny, storna a cesta', 'ostatní S, W, T05 až T09, T11'],
  ['marketing', 'Marketing a rozesílky', 'M*, H*, rozesílky BC'],
];

export function SenderGroups({
  brand,
  canWrite,
}: {
  brand: BrandItem;
  canWrite: boolean;
}): JSX.Element {
  const client = useQueryClient();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const refresh = (): void => {
    setDraft({});
    void client.invalidateQueries({ queryKey: ['brands'] });
  };
  const save = useMutation({
    mutationFn: (group: string) => {
      const email = (draft[group] ?? brand.senderGroups?.[group]?.email ?? '').trim();
      return api.put(`/api/brands/${brand.code}/senders/${group}`, { email });
    },
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (err: Error) => setError(err.message),
  });
  const remove = useMutation({
    mutationFn: (group: string) => api.del(`/api/brands/${brand.code}/senders/${group}`),
    onSuccess: refresh,
    onError: (err: Error) => setError(err.message),
  });
  return (
    <Panel title={`Odesílací identity podle zpráv: ${brand.code}`}>
      <p className="hint">
        Bez nastavení odchází zpráva z odesílatele značky ({brand.sender.email}). Adresa pro
        odpovědi je stejná jako odesílatel. Obě musí být ověřené ve SmartEmailingu.
      </p>
      {error ? <p className="hint">{error}</p> : null}
      <table>
        <thead>
          <tr>
            <th>Skupina</th>
            <th>Kódy</th>
            <th>Odesílatel a odpovědi</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {GROUPS.map(([group, label, codes]) => (
            <tr key={group}>
              <td>{label}</td>
              <td>{codes}</td>
              <td>
                <input
                  disabled={!canWrite}
                  value={draft[group] ?? brand.senderGroups?.[group]?.email ?? ''}
                  placeholder={brand.sender.email}
                  onChange={(e) => setDraft({ ...draft, [group]: e.target.value })}
                />
              </td>
              <td>
                {canWrite ? (
                  <>
                    <button
                      className="btn"
                      onClick={() => save.mutate(group)}
                      disabled={draft[group] === undefined}
                    >
                      Uložit
                    </button>
                    {brand.senderGroups?.[group] ? (
                      <button className="btn" onClick={() => remove.mutate(group)}>
                        Zrušit
                      </button>
                    ) : null}
                  </>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}
